import React from "react";
import { css } from "@linaria/core";
import { useProjects } from "../../../../api/useProjects";
import { FilterWrapper, NONE_LABEL, buildOptions, useSubStateSetter } from "@ui/Filter";
import FilterSelect from "@ui/Filter/FilterSelect";
import t from "../../../../i18n";
import Switch from "@ui/Switch";
import Reset from "@ui/Filter/Reset";
import { useUser } from "../../../../api/useUser";
import { permissionsByRole } from "diagnostics-domain/Permissions";
import { useTranslations } from "../../../../api/useTranslations";
import { useProjectsDisplayDraft, useProjectsFilter } from "../../../../api/useReporting";

const FilterContainer = css`
  align-items: center;
  column-gap: 24px;
  display: flex;
  flex-grow: 1;
`;

const DEFAULT_FILTER_VALUES = {
  ids: [],
  siteIds: [],
  typeIds: [],
};

function ReportingSharedFilters() {
  const user = useUser();
  const projects = useProjects();
  const translations = useTranslations();
  const [displayDraft, setDisplayDraft] = useProjectsDisplayDraft();
  const hasDraftProjects = projects.some(p => p.draft);

  const includedProjects = React.useMemo(
    () =>
      projects.filter(({ id, draft }) => {
        const { projects: projectRoles, global } = user.roles;
        const projectRole = projectRoles.find(({ projectId }) => projectId === id);
        const allow = projectRole
          ? permissionsByRole[projectRole.role].includes("PROJECT_READ")
          : global
            ? permissionsByRole[global].includes("PROJECT_READ")
            : true;

        return !draft || (displayDraft && draft && allow);
      }),
    [displayDraft, projects, user],
  );

  const projectOptions = React.useMemo(
    () =>
      buildOptions(
        includedProjects.map(p => ({
          label: p.name,
          value: p.id.toString(),
        })),
      ),
    [includedProjects],
  );

  const projectTypeOptions = React.useMemo(() => {
    const allOptions = includedProjects.map(p => ({
      label: p.typeId ?? NONE_LABEL,
      value: p.typeId ?? null,
    }));
    return buildOptions(allOptions);
  }, [includedProjects]);

  const projectSiteOptions = React.useMemo(() => {
    const allOptions = includedProjects.map(p => ({
      label: p.siteId ?? NONE_LABEL,
      value: p.siteId ?? null,
    }));
    return buildOptions(allOptions);
  }, [includedProjects]);

  const [projectFilters, setProjectFilters] = useProjectsFilter({});
  const resetFilterValues = React.useCallback(
    () => setProjectFilters({ ...projectFilters, ...DEFAULT_FILTER_VALUES }),
    [projectFilters, setProjectFilters],
  );
  const filterValues = React.useMemo(
    () => ({
      ids: projectFilters.ids,
      siteIds: projectFilters.siteIds,
      typeIds: projectFilters.typeIds,
    }),
    [projectFilters],
  );

  return (
    <div className={FilterContainer}>
      <FilterWrapper>
        <FilterSelect<string | null>
          title={t("administration.quantities.table.header.project_type", translations)}
          options={projectTypeOptions}
          filtered
          values={filterValues.typeIds}
          setValues={useSubStateSetter(setProjectFilters, "typeIds")}
        />
        <FilterSelect<string | null>
          title={t("administration.quantities.table.header.project_site")}
          options={projectSiteOptions}
          filtered
          values={filterValues.siteIds}
          setValues={useSubStateSetter(setProjectFilters, "siteIds")}
        />
        <FilterSelect<string>
          title={t("administration.quantities.table.header.project_name")}
          options={projectOptions}
          filtered
          values={filterValues.ids}
          setValues={useSubStateSetter(setProjectFilters, "ids")}
        />
        <Reset
          filterValues={filterValues}
          setFilterValues={resetFilterValues}
          defaultFilterValues={DEFAULT_FILTER_VALUES}
        />
      </FilterWrapper>
      {hasDraftProjects && (
        <Switch checked={displayDraft} onChange={e => setDisplayDraft(e.currentTarget.checked)}>
          {t("reporting.header.display_draft")}
        </Switch>
      )}
    </div>
  );
}

export default React.memo(ReportingSharedFilters);
